/**
 * Navigation Service - Real Turn-by-Turn Directions
 * Integrates with Google Directions API for actual road navigation
 * Includes offline caching and last-mile compass navigation
 */

export interface NavigationStep {
  instruction: string;
  htmlInstruction?: string;
  distance: number; // meters
  duration: number; // seconds
  startLocation: { lat: number; lng: number };
  endLocation: { lat: number; lng: number };
  maneuver?: string; // 'turn-right', 'turn-left', 'straight', etc.
  polyline?: string; // Encoded polyline for the step
}

export interface NavigationRoute {
  steps: NavigationStep[];
  totalDistance: number; // meters
  totalDuration: number; // seconds
  bounds: {
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
  };
  polyline: string; // Full route polyline
  warnings: string[];
  copyrights: string;
}

export interface NavigationState {
  currentStepIndex: number;
  distanceToNextStep: number; // meters
  distanceToDestination: number; // meters
  etaMinutes: number;
  currentSpeed: number; // mph
  currentHeading: number; // degrees
  isOffRoute: boolean;
  isLastMile: boolean; // When roads end, switch to compass mode
  compassBearing: number; // Direct bearing to well
}

export interface WellCheckRecord {
  id: string;
  wellId: string;
  wellName: string;
  pumperId: string;
  visitorId: string;
  pumperName: string;
  checkInTime: Date;
  checkOutTime?: Date;
  location: { lat: number; lng: number };
  distanceFromWell: number; // feet
  verificationMethod: 'gps' | 'manual';
  notes?: string;
  dataEntryCompleted: boolean;
}

// Constants
const EARTH_RADIUS_METERS = 6371000;
const METERS_TO_MILES = 0.000621371;
const METERS_TO_FEET = 3.28084;
const OFF_ROUTE_THRESHOLD_METERS = 100; // 100m = ~330 feet
const LAST_MILE_THRESHOLD_METERS = 500; // Switch to compass when within 500m with no more road steps
const ARRIVAL_THRESHOLD_FEET = 150; // Consider arrived within 150 feet

class NavigationService {
  private apiKey: string | null = null;
  private cachedRoutes: Map<string, { route: NavigationRoute; timestamp: number }> = new Map();
  private readonly CACHE_DURATION_MS = 1000 * 60 * 60; // 1 hour cache

  constructor() {
    this.apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || null;
    this.loadCachedRoutes();
  }

  /**
   * Get directions from current location to destination
   */
  async getDirections(
    origin: { lat: number; lng: number },
    destination: { lat: number; lng: number },
    options: {
      avoidTolls?: boolean;
      avoidHighways?: boolean;
      departureTime?: Date;
    } = {}
  ): Promise<NavigationRoute | null> {
    // Check cache first
    const cacheKey = `${origin.lat},${origin.lng}-${destination.lat},${destination.lng}`;
    const cached = this.cachedRoutes.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION_MS) {
      return cached.route;
    }

    if (!this.apiKey) {
      return this.createFallbackRoute(origin, destination);
    }

    try {
      const url = new URL('https://maps.googleapis.com/maps/api/directions/json');
      url.searchParams.append('origin', `${origin.lat},${origin.lng}`);
      url.searchParams.append('destination', `${destination.lat},${destination.lng}`);
      url.searchParams.append('mode', 'driving');
      url.searchParams.append('units', 'imperial');
      url.searchParams.append('key', this.apiKey);

      if (options.avoidTolls) url.searchParams.append('avoid', 'tolls');
      if (options.avoidHighways) url.searchParams.append('avoid', 'highways');
      if (options.departureTime) {
        url.searchParams.append('departure_time', Math.floor(options.departureTime.getTime() / 1000).toString());
      }

      // Note: In production, this should go through a backend to protect the API key
      // For now, we'll use the client-side approach
      const response = await fetch(url.toString());
      const data = await response.json();

      if (data.status !== 'OK' || !data.routes || data.routes.length === 0) {
        return this.createFallbackRoute(origin, destination);
      }

      const route = this.parseDirectionsResponse(data);
      
      // Cache the route
      this.cachedRoutes.set(cacheKey, { route, timestamp: Date.now() });
      this.saveCachedRoutes();

      return route;
    } catch (error) {
      console.error('Error fetching directions:', error);
      return this.createFallbackRoute(origin, destination);
    }
  }

  /**
   * Parse Google Directions API response into our format
   */
  private parseDirectionsResponse(data: any): NavigationRoute {
    const route = data.routes[0];
    const leg = route.legs[0]; // Single destination

    const steps: NavigationStep[] = leg.steps.map((step: any) => ({
      instruction: this.stripHtml(step.html_instructions),
      htmlInstruction: step.html_instructions,
      distance: step.distance.value,
      duration: step.duration.value,
      startLocation: step.start_location,
      endLocation: step.end_location,
      maneuver: step.maneuver || 'straight',
      polyline: step.polyline?.points,
    }));

    return {
      steps,
      totalDistance: leg.distance.value,
      totalDuration: leg.duration.value,
      bounds: route.bounds,
      polyline: route.overview_polyline.points,
      warnings: route.warnings || [],
      copyrights: route.copyrights || '© Google',
    };
  }

  /**
   * Create a fallback route when API is unavailable
   * Uses compass-based navigation
   */
  private createFallbackRoute(
    origin: { lat: number; lng: number },
    destination: { lat: number; lng: number }
  ): NavigationRoute {
    const distance = this.calculateDistance(origin.lat, origin.lng, destination.lat, destination.lng);
    const bearing = this.calculateBearing(origin.lat, origin.lng, destination.lat, destination.lng);
    const direction = this.bearingToDirection(bearing);

    const steps: NavigationStep[] = [
      {
        instruction: `Head ${direction} toward destination`,
        distance: distance,
        duration: Math.round((distance * METERS_TO_MILES) / 45 * 3600), // Assume 45mph
        startLocation: origin,
        endLocation: destination,
        maneuver: 'straight',
      },
    ];

    return {
      steps,
      totalDistance: distance,
      totalDuration: steps[0].duration,
      bounds: {
        northeast: {
          lat: Math.max(origin.lat, destination.lat),
          lng: Math.max(origin.lng, destination.lng),
        },
        southwest: {
          lat: Math.min(origin.lat, destination.lat),
          lng: Math.min(origin.lng, destination.lng),
        },
      },
      polyline: '',
      warnings: ['Using compass navigation - GPS directions unavailable'],
      copyrights: '',
    };
  }

  /**
   * Calculate navigation state based on current position
   */
  calculateNavigationState(
    currentPosition: { lat: number; lng: number; heading?: number; speed?: number },
    route: NavigationRoute,
    destination: { lat: number; lng: number }
  ): NavigationState {
    // Find closest step
    let closestStepIndex = 0;
    let minDistance = Infinity;

    for (let i = 0; i < route.steps.length; i++) {
      const step = route.steps[i];
      const distToStep = this.calculateDistance(
        currentPosition.lat,
        currentPosition.lng,
        step.endLocation.lat,
        step.endLocation.lng
      );
      if (distToStep < minDistance) {
        minDistance = distToStep;
        closestStepIndex = i;
      }
    }

    // Calculate distance to current step end
    const currentStep = route.steps[closestStepIndex];
    const distanceToNextStep = this.calculateDistance(
      currentPosition.lat,
      currentPosition.lng,
      currentStep.endLocation.lat,
      currentStep.endLocation.lng
    );

    // Calculate total remaining distance
    let remainingDistance = distanceToNextStep;
    for (let i = closestStepIndex + 1; i < route.steps.length; i++) {
      remainingDistance += route.steps[i].distance;
    }

    // Direct distance to destination
    const directDistance = this.calculateDistance(
      currentPosition.lat,
      currentPosition.lng,
      destination.lat,
      destination.lng
    );

    // Calculate compass bearing to destination
    const compassBearing = this.calculateBearing(
      currentPosition.lat,
      currentPosition.lng,
      destination.lat,
      destination.lng
    );

    // Check if off route
    const distanceFromRoute = this.calculateDistanceFromRoute(currentPosition, route, closestStepIndex);
    const isOffRoute = distanceFromRoute > OFF_ROUTE_THRESHOLD_METERS;

    // Check if in last-mile mode (close but no more road directions)
    const isLastMile = directDistance < LAST_MILE_THRESHOLD_METERS && 
                       closestStepIndex >= route.steps.length - 1;

    // Speed in mph (convert from m/s if provided)
    const currentSpeed = currentPosition.speed 
      ? currentPosition.speed * 2.237 // m/s to mph
      : 0;

    // ETA based on remaining distance at current speed (or 45mph default)
    const effectiveSpeed = currentSpeed > 5 ? currentSpeed : 45;
    const etaMinutes = Math.round((remainingDistance * METERS_TO_MILES) / effectiveSpeed * 60);

    return {
      currentStepIndex: closestStepIndex,
      distanceToNextStep,
      distanceToDestination: remainingDistance,
      etaMinutes,
      currentSpeed,
      currentHeading: currentPosition.heading || 0,
      isOffRoute,
      isLastMile,
      compassBearing,
    };
  }

  /**
   * Calculate distance from current position to route line
   */
  private calculateDistanceFromRoute(
    position: { lat: number; lng: number },
    route: NavigationRoute,
    currentStepIndex: number
  ): number {
    const step = route.steps[currentStepIndex];
    if (!step) return 0;

    // Simple perpendicular distance to line segment
    return this.pointToLineDistance(
      position,
      step.startLocation,
      step.endLocation
    );
  }

  /**
   * Calculate perpendicular distance from point to line segment
   */
  private pointToLineDistance(
    point: { lat: number; lng: number },
    lineStart: { lat: number; lng: number },
    lineEnd: { lat: number; lng: number }
  ): number {
    const A = this.calculateDistance(point.lat, point.lng, lineStart.lat, lineStart.lng);
    const B = this.calculateDistance(point.lat, point.lng, lineEnd.lat, lineEnd.lng);
    const C = this.calculateDistance(lineStart.lat, lineStart.lng, lineEnd.lat, lineEnd.lng);

    if (C === 0) return A;

    // Using Heron's formula
    const s = (A + B + C) / 2;
    const area = Math.sqrt(Math.max(0, s * (s - A) * (s - B) * (s - C)));
    const height = (2 * area) / C;

    return height;
  }

  /**
   * Haversine distance calculation
   */
  calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS_METERS * c;
  }

  /**
   * Calculate bearing between two points
   */
  calculateBearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const dLng = this.toRad(lng2 - lng1);
    const lat1Rad = this.toRad(lat1);
    const lat2Rad = this.toRad(lat2);

    const y = Math.sin(dLng) * Math.cos(lat2Rad);
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
      Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);

    let bearing = Math.atan2(y, x);
    bearing = this.toDeg(bearing);
    bearing = (bearing + 360) % 360;

    return bearing;
  }

  /**
   * Convert bearing to cardinal direction
   */
  bearingToDirection(bearing: number): string {
    const directions = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];
    const index = Math.round(bearing / 45) % 8;
    return directions[index];
  }

  /**
   * Get maneuver icon name based on maneuver type
   */
  getManeuverIcon(maneuver: string): string {
    const iconMap: Record<string, string> = {
      'turn-left': 'ArrowLeft',
      'turn-right': 'ArrowRight',
      'turn-slight-left': 'ArrowUpLeft',
      'turn-slight-right': 'ArrowUpRight',
      'turn-sharp-left': 'CornerLeftDown',
      'turn-sharp-right': 'CornerRightDown',
      'uturn-left': 'RotateCcw',
      'uturn-right': 'RotateCw',
      'straight': 'ArrowUp',
      'ramp-left': 'ArrowUpLeft',
      'ramp-right': 'ArrowUpRight',
      'merge': 'Merge',
      'fork-left': 'GitBranch',
      'fork-right': 'GitBranch',
      'roundabout-left': 'RotateCcw',
      'roundabout-right': 'RotateCw',
      'arrive': 'Flag',
    };
    return iconMap[maneuver] || 'ArrowUp';
  }

  /**
   * Format distance for display
   */
  formatDistance(meters: number): string {
    const miles = meters * METERS_TO_MILES;
    if (miles < 0.1) {
      const feet = Math.round(meters * METERS_TO_FEET);
      return `${feet} ft`;
    } else if (miles < 10) {
      return `${miles.toFixed(1)} mi`;
    } else {
      return `${Math.round(miles)} mi`;
    }
  }

  /**
   * Format duration for display
   */
  formatDuration(seconds: number): string {
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMins = minutes % 60;
      return `${hours}h ${remainingMins}m`;
    }
  }

  /**
   * Check if arrived at destination
   */
  isArrived(
    currentPosition: { lat: number; lng: number },
    destination: { lat: number; lng: number }
  ): boolean {
    const distance = this.calculateDistance(
      currentPosition.lat,
      currentPosition.lng,
      destination.lat,
      destination.lng
    );
    const distanceFeet = distance * METERS_TO_FEET;
    return distanceFeet < ARRIVAL_THRESHOLD_FEET;
  }

  /**
   * Strip HTML tags from instruction
   */
  private stripHtml(html: string): string {
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/  +/g, ' ')
      .trim();
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  private toDeg(rad: number): number {
    return rad * (180 / Math.PI);
  }

  /**
   * Cache management
   */
  private loadCachedRoutes(): void {
    try {
      const cached = localStorage.getItem('navigation_routes_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        this.cachedRoutes = new Map(Object.entries(parsed));
      }
    } catch (error) {
    }
  }

  private saveCachedRoutes(): void {
    try {
      const obj: Record<string, any> = {};
      this.cachedRoutes.forEach((value, key) => {
        obj[key] = value;
      });
      localStorage.setItem('navigation_routes_cache', JSON.stringify(obj));
    } catch (error) {
    }
  }

  clearCache(): void {
    this.cachedRoutes.clear();
    localStorage.removeItem('navigation_routes_cache');
  }
}

// Singleton export
export const navigationService = new NavigationService();
export default navigationService;
